import { useState, useRef, useEffect } from "react";
import { Button, Input } from "@headerhawk/ui";
import { HostListField } from "@/components/settings/host-list-field";
import {
  validateSiteForm,
  hasErrors,
  hostRejectionsOf,
  rowErrorsFromRejections,
  rowsToPatterns,
  type HostRow,
  type SiteFormData,
  type SiteValidationErrors,
} from "@/lib/validation/site";

interface SiteFormProps {
  /**
   * Create the site. A rejection whose `400` names refused hosts is shown
   * beside the host field; any other rejection is the caller's to report.
   */
  onSubmit: (data: SiteFormData) => Promise<void>;
  isSubmitting: boolean;
  onBack?: () => void;
  submitLabel?: string;
  defaultValues?: { name?: string; host?: string };
  disabled?: boolean;
}

export function SiteForm({
  onSubmit,
  isSubmitting,
  onBack,
  submitLabel = "Create Site",
  defaultValues,
  disabled = false,
}: SiteFormProps) {
  const [name, setName] = useState(defaultValues?.name ?? "");
  const [rows, setRows] = useState<HostRow[]>([
    { host: defaultValues?.host ?? "", includeSubdomains: false },
  ]);
  const [errors, setErrors] = useState<SiteValidationErrors>({});
  const [touched, setTouched] = useState<{ name?: boolean; hosts?: boolean }>(
    {},
  );

  const nameInputRef = useRef<HTMLInputElement>(null);
  const hostInputRef = useRef<HTMLInputElement>(null);

  // Focus first input on mount (if not disabled)
  useEffect(() => {
    if (!disabled && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [disabled]);

  const revalidate = (next: { name: string; hosts: HostRow[] }) => {
    const all = validateSiteForm(next);
    setErrors((prev) => ({
      name: touched.name ? all.name : prev.name,
      hosts: touched.hosts ? all.hosts : prev.hosts,
      hostRows: touched.hosts ? all.hostRows : prev.hostRows,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateSiteForm({ name, hosts: rows });
    setErrors(validationErrors);
    setTouched({ name: true, hosts: true });

    if (hasErrors(validationErrors)) {
      // Focus the first field with an error
      if (validationErrors.name && nameInputRef.current) {
        nameInputRef.current.focus();
      } else if (hostInputRef.current) {
        hostInputRef.current.focus();
      }
      return;
    }

    const { patterns, rowOf } = rowsToPatterns(rows);
    try {
      await onSubmit({ name: name.trim(), hosts: patterns });
    } catch (error) {
      const rejections = hostRejectionsOf(error);
      if (!rejections) throw error;
      setErrors({ hostRows: rowErrorsFromRejections(rejections, rowOf) });
      hostInputRef.current?.focus();
    }
  };

  const showNameError = touched.name && errors.name;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label
          htmlFor="site-name"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Site name
        </label>
        <Input
          ref={nameInputRef}
          id="site-name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (touched.name) revalidate({ name: e.target.value, hosts: rows });
          }}
          onBlur={() => {
            setTouched((prev) => ({ ...prev, name: true }));
            setErrors((prev) => ({
              ...prev,
              name: validateSiteForm({ name, hosts: rows }).name,
            }));
          }}
          placeholder="My Website"
          maxLength={100}
          disabled={isSubmitting || disabled}
          aria-describedby={showNameError ? "name-error" : "name-hint"}
          aria-invalid={showNameError ? true : undefined}
        />
        {showNameError ? (
          <p id="name-error" className="text-sm text-destructive">
            {errors.name}
          </p>
        ) : (
          <p id="name-hint" className="text-sm text-muted-foreground">
            A friendly name to identify this site
          </p>
        )}
      </div>

      <HostListField
        idPrefix="site"
        rows={rows}
        inputRef={hostInputRef}
        onChange={(next) => {
          setRows(next);
          if (touched.hosts) revalidate({ name, hosts: next });
        }}
        onBlur={() => {
          setTouched((prev) => ({ ...prev, hosts: true }));
          const all = validateSiteForm({ name, hosts: rows });
          setErrors((prev) => ({
            ...prev,
            hosts: all.hosts,
            hostRows: all.hostRows,
          }));
        }}
        listError={touched.hosts ? errors.hosts : undefined}
        rowErrors={errors.hostRows}
        disabled={isSubmitting || disabled}
      />

      <div className="flex justify-between pt-4">
        {onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Back
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting || disabled}
          className={!onBack ? "ml-auto" : ""}
        >
          {isSubmitting ? "Creating..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}
